// Clean re-implementation (classic mode only) with: turn popup, toasts (leader/cheer/turn),
// auto scroll, early score guard, all-player highscore save, roll counter fix, new game confirm.
import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import GlowEffects from './GlowEffects';
import { Player, MultiplayerGameState } from '../types/multiplayer';
import { YatzyScores, Die as DieType } from '../types/game';
import StrategyHelper from './StrategyHelper';
import { toggleDieLock, resetDice, calculatePossibleScore, calculateTotalScore } from '../utils/gameLogic';
import MaxiScoreCard from './MaxiScoreCard';
import { MaxiYatzyScores } from '../types/maxi';
import { calculateMaxiTotals, calculateMaxiScore, maxiScoreCategories } from '../utils/maxiGameLogic';
import { scoreCategories } from '../utils/gameLogic';
import { soundManager } from '../utils/soundManager';
import { addHighScore, isHighScore, submitGlobalHighScore } from '../utils/highScore';
import { markPlayedToday } from '../utils/notifications';
import { decideTurnAction, decideFinalCategory, AILevel } from '../utils/aiPlayer';
import { computeDicePositions } from '../utils/dicePositions';
import Die2D from './Die2D';
import DiceBoard3D from './DiceBoard3D';
import ScoreCard from './ScoreCard';
import PlayerStatusDisplay from './PlayerStatusDisplay';
import HighScoreList from './HighScoreList';
import MobileMenu from './MobileMenu';
import { useToast } from '../context/ToastContext';
import GameOverModal from './GameOverModal';
import StatisticsModal from './StatisticsModal';
import HelpRulesModal from './HelpRulesModal';
import AchievementsModal from './AchievementsModal';
import FunFactsModal from './FunFactsModal';
import ChangelogModal from './ChangelogModal';
import {
  PositionedDie,
  SidebarSection,
  FabContainer,
  SpeedDialWrapper,
  DialActions,
  DialBtn,
} from './shared/GameStyles';

interface MultiplayerGameTestOverrides {
  rollDelayMs?: number;          // override roll animation delay (default 500ms)
  disableToasts?: boolean;       // disable leader/cheer/turn toasts for test determinism
  onSelectCategory?: (category:string, accepted:boolean, rollsLeft:number)=>void; // observe score attempts in tests
  bypassUISelect?: (category:string)=>boolean; // if returns true, invoke scoring logic without UI click path
  autoSelectCategory?: string; // test hook: automatically attempt to select this category (after potential roll adjust)
  initialRollsLeft?: number;   // test hook: set rollsLeft immediately after mount
}
interface MultiplayerGameProps { players: Player[]; onGameEnd: () => void; testOverrides?: MultiplayerGameTestOverrides; isForcedMode?: boolean; /** internal: hide menu (tests) */ hideMenu?: boolean; }

/* Animations */
const pulse = keyframes`0%{transform:scale(1);}50%{transform:scale(1.04);}100%{transform:scale(1);}`;
const toastIn = keyframes`0%{transform:translateY(-6px);opacity:0;}100%{transform:translateY(0);opacity:1;}`;
const toastOut = keyframes`0%{transform:translateY(0);opacity:1;}100%{transform:translateY(-4px);opacity:0;}`;

/* Layout */
const GameContainer = styled.div`
  min-height: 100vh;
  background: var(--theme-background, linear-gradient(135deg, var(--theme-gradient-start, #1a237e) 0%, var(--theme-gradient-end, #311b92) 100%));
  padding: calc(env(safe-area-inset-top,0) + 24px) 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: background 0.5s ease;
  overflow-x: hidden;
  @media (max-width: 768px) {
    /* Reserve space for fixed top FAB buttons so they never overlap content. */
    padding: calc(env(safe-area-inset-top,0) + 74px) 16px 16px;
  }
`;
const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
  color: #fff;
  position: relative;
  z-index: 1;
`;

const Title = styled.h1`
  font-size: clamp(32px, 5vw, 48px);
  margin: 0 0 8px;
  text-shadow: 
    0 2px 4px rgba(0,0,0,0.3),
    0 4px 8px rgba(0,0,0,0.2);
  letter-spacing: -0.5px;
  font-weight: 800;
  
  line-height: 1.08;
`;

const Subtitle = styled.p`
  font-size: clamp(14px, 2.1vw, 18px);
  margin: 0;
  opacity: 0.95;
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
  font-weight: 500;
  letter-spacing: 0.2px;
`;
const GameBoard = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  width: 100%;
  max-width: 1240px;
  align-self: stretch;
  margin: 0 auto;
  scroll-margin-top: calc(env(safe-area-inset-top, 0px) + 20px);
  
  @media (min-width: 768px) {
    /* CHANGE (2026-08-15): Bredare tärningskolumn – spelplanen var för liten
       för att 5–6 tärningar skulle kunna läsas av. */
    grid-template-columns: minmax(420px, 620px) minmax(400px, 1fr);
    align-items: start;
    gap: clamp(18px, 3vw, 34px);
    scroll-margin-top: 20px;
  }
  
  @media (max-width: 768px) {
    padding-bottom: calc(80px + env(safe-area-inset-bottom));
    /* Smooth scroll behavior för hela spelplanen */
    scroll-behavior: smooth;
  }
`;
const DiceSection = styled.div`
  background: var(--theme-card-background, rgba(255, 255, 255, 0.95));
  border: 2px solid var(--theme-card-border, rgba(255, 255, 255, 0.1));
  border-radius: 24px;
  padding: 30px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.2),
    0 4px 16px rgba(0, 0, 0, 0.15),
    inset 0 1px 1px rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(12px) saturate(180%);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 320px;
  transition: all 0.5s ease;
  width: 100%;
  max-width: 100%;
  
  @media (min-width: 768px) {
    width: min(620px, 100%);
    min-width: 0;
    justify-self: center;
  }
  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 20px;
  }
  
  /* Sticky tärningar i landskapsläge på telefoner */
  @media (max-height: 600px) and (min-width: 600px) {
    position: sticky;
    top: 20px;
    z-index: 10;
    margin-bottom: 20px;
  }
`;

const DiceContainer = styled.div<{ $tall?: boolean }>`
  /* Modernized felt-style background with enhanced visual effects */
  position: relative;
  width: 100%;
  max-width: 600px;
  /* CHANGE (2026-08-15): Högre plan så att tärningarna får plats på flera
     rader utan att hamna ovanpå varandra (se utils/dicePositions). */
  height: ${p => p.$tall ? '460px' : '300px'};
  margin: 0 auto 18px;
  border-radius: 24px;
  background:
    radial-gradient(circle at 30% 28%, rgba(255,255,255,0.12), transparent 55%),
    radial-gradient(circle at 72% 65%, rgba(255,255,255,0.08), transparent 60%),
    linear-gradient(140deg, 
      var(--felt-dark-color, #0a5226) 0%, 
      var(--felt-base-color, #137a38) 55%, 
      var(--felt-light-color, #1a9445) 100%
    );
  box-shadow:
    inset 0 2px 4px rgba(255,255,255,0.2),
    inset 0 -2px 4px rgba(0,0,0,0.3),
    0 8px 24px -6px rgba(0,0,0,0.4),
    0 4px 8px rgba(0,0,0,0.2);
  border: 2px solid var(--felt-edge-color,#062d15);
  outline: 1px solid rgba(255,255,255,0.06);
  overflow: hidden;
  
  /* Texture overlay */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(255,255,255,0.02) 2px, transparent 2px),
      radial-gradient(circle at 75% 75%, rgba(0,0,0,0.02) 1px, transparent 1px);
    background-size: 40px 40px, 28px 28px;
    pointer-events: none;
  }
  
  @media (max-width: 640px) { height: ${p => p.$tall ? '380px' : '260px'}; }
  @media (max-width: 520px) {
    /* Behåller spelplansbakgrunden även på smala skärmar */
    height: auto; 
    min-height: 100px; 
    display: flex; 
    flex-wrap: nowrap; 
    overflow-x: auto; 
    gap: 12px; 
    padding: 12px 8px; 
    align-items: center; 
    justify-content: center;
    /* Säkerställer att background syns även på smala skärmar */
    background: var(--theme-dice-area-background, 
      linear-gradient(140deg, var(--felt-dark-color,#094921) 0%, var(--felt-base-color,#0f6d2f) 55%, var(--felt-light-color,#158a3c) 100%)
    ) !important;
    & > div { position: static !important; transform: none !important; }
  }
`;

const GameInfo = styled.div`
  text-align: center;
  margin-bottom: 24px;
  color: var(--theme-text, #1a1a1a);
`;

const RollInfo = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 10px;
  color: var(--theme-secondary, #1976D2);
  text-shadow: 0 1px 1px rgba(0,0,0,0.05);
  letter-spacing: 0.2px;
`;

const RoundInfo = styled.div`
  font-size: 16px;
  color: var(--theme-text-secondary, #555);
  font-weight: 500;
`;
// Shared dice layouts (responsive)
const SmallDiceRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(56px, 1fr));
  gap: 10px;
  width: 100%;
  max-width: 420px;
  margin: 0 auto 18px;
  padding: 10px 6px 14px;
  align-items: center;
  justify-items: center;
  min-height: 118px;
`;
const ButtonContainer = styled.div`display:flex;gap:15px;flex-wrap:wrap;justify-content:center;`;
const Button = styled.button<{ $variant?:'primary'|'secondary'|'danger';variant?:'primary'|'secondary'|'danger' }>`
  background: ${p=>{
    const v=p.$variant||p.variant;
    if(v==='danger') return 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)';
    if(v==='secondary') return 'linear-gradient(135deg, #757575 0%, #616161 100%)';
    return 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)';
  }};
  color: #fff;
  border: none;
  border-radius: 16px;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.3px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    background: linear-gradient(135deg, #ccc 0%, #999 100%);
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  @media (max-width: 768px) {
    flex: 1 1 auto;
    min-width: 120px;
    padding: 12px 24px;
  }
`;
const TurnBanner = styled.div`
  background: #FFF8E1;
  border: 1px solid #FFE082;
  color: #E65100;
  padding: 10px 16px;
  border-radius: 999px;
  margin-bottom: 10px;
  font-weight: 700;
  animation: ${pulse} .6s;
  max-width: 92vw;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Toast / Popup
/* CHANGE (2026-08-15): Notiserna låg 10px från toppen och täckte rubriken
   ("<spelare>s tur – Kast X av Y"). De ligger nu nedtill, där de inte skymmer
   något viktigt (pointer-events:none, så knapparna under går att klicka). */
const ToastStack = styled.div`position:fixed;bottom:calc(env(safe-area-inset-bottom,0) + 18px);left:50%;transform:translateX(-50%);z-index:1400;display:flex;flex-direction:column-reverse;gap:8px;width:min(94%,480px);pointer-events:none;align-items:center;`;
const ToastMsg = styled.div<{ $leaving?:boolean;$variant:'leader'|'cheer'|'turn' }>`
  background:${p=>p.$variant==='leader'?'linear-gradient(135deg,#263238,#37474f)':p.$variant==='cheer'?'linear-gradient(135deg,#1b5e20,#2e7d32)':'linear-gradient(135deg,#01579b,#0277bd)'};
  color:#fff;
  font-weight:600;
  font-size:14px;
  padding:12px 16px;
  border-radius:12px;
  box-shadow:0 8px 20px -6px rgba(0,0,0,.5),0 2px 6px rgba(0,0,0,.4);
  border:1px solid rgba(255,255,255,0.18);
  backdrop-filter:blur(14px) saturate(160%);
  animation:${toastIn} .45s ease, ${p=>p.$leaving?toastOut:toastIn} .4s ease ${p=>p.$leaving?'.45s':''} forwards;
`;
const Overlay = styled.div<{ $show:boolean }>`position:fixed;inset:0;display:${p=>p.$show?'flex':'none'};align-items:center;justify-content:center;z-index:1500;background:rgba(0,0,0,0.55);padding:30px;`;
const PopupCard = styled.div`background:rgba(30,30,40,0.92);color:#fff;padding:38px 34px 34px;border-radius:26px;width:min(92%,460px);text-align:center;position:relative;border:1px solid rgba(255,255,255,0.18);box-shadow:0 16px 40px -10px rgba(0,0,0,.7),0 6px 18px rgba(0,0,0,.4);backdrop-filter:blur(24px) saturate(180%);`;
const PopupTitle = styled.h2`margin:0 0 14px;font-size:30px;letter-spacing:.5px;`;
const PopupText = styled.div`font-size:16px;opacity:.9;line-height:1.5;margin-bottom:26px;`;
const PopupButton = styled.button`background:linear-gradient(135deg,#ff9800,#ffc107);color:#212121;font-weight:700;border:none;padding:15px 28px;font-size:16px;border-radius:14px;cursor:pointer;letter-spacing:.5px;width:100%;box-shadow:0 6px 16px -4px rgba(0,0,0,.55);transition:transform .25s,box-shadow .25s;&:hover{transform:translateY(-2px);box-shadow:0 10px 20px -6px rgba(0,0,0,.6);} &:active{transform:translateY(0);}`;
const PopupActions = styled.div`display:flex;flex-direction:column;gap:14px;margin-top:4px;`;

// Speed Dial Components
const Fab = styled.button<{ $pos: 'tl' | 'tr' | 'bl' | 'br' }>`
  position: fixed;
  z-index: 1000;
  ${(p) => p.$pos === 'tl' && 'top:calc(20px + env(safe-area-inset-top, 0));left:calc(20px + env(safe-area-inset-left, 0));'}
  ${(p) => p.$pos === 'tr' && 'top:calc(20px + env(safe-area-inset-top, 0));right:calc(20px + env(safe-area-inset-right, 0));'}
  ${(p) => p.$pos === 'bl' && 'bottom:calc(20px + env(safe-area-inset-bottom, 0));left:calc(20px + env(safe-area-inset-left, 0));'}
  ${(p) => p.$pos === 'br' && 'bottom:calc(20px + env(safe-area-inset-bottom, 0));right:calc(20px + env(safe-area-inset-right, 0));'}
  
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 600;
  background: var(--theme-primary, #4CAF50);
  color: white;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2), 0 3px 8px rgba(76, 175, 80, 0.3);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 640px) {
    width: 48px;
    height: 48px;
    font-size: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.24);
  }
  
  &:hover {
    box-shadow: 0 12px 32px rgba(0,0,0,0.3), 0 6px 12px rgba(76,175,80,0.4);
    transform: scale(1.08);
  }
`;

const MainDial = styled.button`
  width: 64px;
  height: 64px;
  font-size: 30px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  background: var(--theme-primary, #4CAF50);
  color: white;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2), 0 3px 8px rgba(76, 175, 80, 0.3);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
  transform: translateZ(0);
  position: relative;
  
  &:hover {
    transform: translateZ(0) scale(1.05);
    box-shadow: 0 12px 32px rgba(0,0,0,0.3), 0 6px 12px rgba(76,175,80,0.4);
  }
  
  &:active {
    transform: translateZ(0) scale(0.95);
  }
  
  @media (max-width: 640px) {
    width: 56px;
    height: 56px;
    font-size: 26px;
  }
`;

// Helper function to roll dice
const rollDice = (dice: DieType[]): DieType[] => {
  return dice.map(die => ({
    ...die,
    value: die.isLocked ? die.value : Math.floor(Math.random() * 6) + 1
  }));
};

const MultiplayerGame: React.FC<MultiplayerGameProps> = ({ players: initialPlayers, onGameEnd, testOverrides, isForcedMode, hideMenu }) => {
  const { showConfirmationToast } = useToast();
  const [gameState, setGameState] = useState<MultiplayerGameState>({ players: initialPlayers, currentPlayerIndex: 0, gamePhase: 'playing' });
  const isMaxi = (() => {
    const first = initialPlayers[0];
    if (!first) return false;
    const keys = Object.keys(first.scores || {});
    return keys.length > 15; // heuristic: maxi has 19 categories
  })();
  const isForced = React.useMemo(() => {
    if (typeof isForcedMode === 'boolean') return isForcedMode;
    return initialPlayers.some(p => (p as any).modeMeta?.forced);
  }, [initialPlayers, isForcedMode]);
  const diceCount = isMaxi ? 6 : 5;
  // Maxi i lokal multiplayer spelas medvetet med 6 kast (se test
  // "uses six rolls in maxi mode UI"); singleplayer-Maxi använder 3.
  // Poängkorten får maxRolls som prop så att kategorier blir valbara efter
  // första kastet oavsett antal kast (tidigare hårdkodat "rollsLeft < 3").
  const maxRolls = isMaxi ? 6 : 3;
  const [dice, setDice] = useState<DieType[]>(resetDice(diceCount as any));
  const [dicePositions, setDicePositions] = useState<{x:number;y:number;}[]>([]);
  const [isNarrow, setIsNarrow] = useState<boolean>(() => (typeof window !== 'undefined' ? window.innerWidth < 520 : true));
  const [isUltraNarrow, setIsUltraNarrow] = useState<boolean>(() => (typeof window !== 'undefined' ? window.innerWidth < 400 : true));
  const diceAreaRef = useRef<HTMLDivElement | null>(null);
  const [rollsLeft, setRollsLeft] = useState(() => maxRolls);
  const [isRolling, setIsRolling] = useState(false);
  const [showHighScores, setShowHighScores] = useState(false);
  const [showGameOver, setShowGameOver] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [justScoredCategory, setJustScoredCategory] = useState<string | null>(null);
  const [turnPopupVisible, setTurnPopupVisible] = useState(false);
  const [confirmNewGame, setConfirmNewGame] = useState(false);
  const [toasts, setToasts] = useState<{id:number; msg:string; variant:'leader'|'cheer'|'turn'; leaving?:boolean}[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Speed dial states
  const [dialOpen, setDialOpen] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showFunFacts, setShowFunFacts] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [aiAssistantEnabled, setAiAssistantEnabled] = useState(() => {
    try {
      return localStorage.getItem('yatzy-ai-assistant-enabled') === 'true';
    } catch {
      return false;
    }
  });
  const toastId = useRef(0);
  const scoreCardRef = useRef<HTMLDivElement | null>(null);
  const currentPlayer = gameState.players[gameState.currentPlayerIndex];

  // Hantera scroll-beteende och popup för varje tur
  useEffect(() => {
    const handleScroll = () => {
      // På mobil, scrolla till toppen först för bättre översikt
      if (window.innerWidth <= 768) {
        try {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          // Vänta kort innan vi scrollar till scorecard för smidigare upplevelse
          setTimeout(() => {
            scoreCardRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          }, 300);
        } catch {}
      } else {
        // På desktop, scrolla direkt till scorecard
        try {
          scoreCardRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        } catch {}
      }
    };

    // Använd en kort delay för att säkerställa att DOM har uppdaterats
    const scrollTimer = setTimeout(handleScroll, 100);
    
    // Visa turn popup för alla utom första spelaren
    if (gameState.currentPlayerIndex !== 0) {
      setTurnPopupVisible(true);
    } else {
      setTurnPopupVisible(false);
    }

    return () => clearTimeout(scrollTimer);
  }, [gameState.currentPlayerIndex]);

  const toastTimersRef = useRef<number[]>([]);
  useEffect(() => {
    return () => {
      toastTimersRef.current.forEach(clearTimeout);
      toastTimersRef.current = [];
    };
  }, []);

  const pushToast = React.useCallback((msg:string, variant:'leader'|'cheer'|'turn') => {
    if (testOverrides?.disableToasts) return;
    const id = ++toastId.current;
    setToasts(t => [...t, {id, msg, variant}]);
    const leavingTimer = window.setTimeout(() => {
      setToasts(t => t.map(o => o.id === id ? {...o, leaving: true} : o));
    }, 2600);
    const removeTimer = window.setTimeout(() => {
      setToasts(t => t.filter(o => o.id !== id));
    }, 3000);
    toastTimersRef.current.push(leavingTimer, removeTimer);
  }, [testOverrides?.disableToasts]);
  const cheerForScore = (score:number) => { if(score>=50) pushToast('🔥 Fantastiskt kast!','cheer'); else if(score>=40) pushToast('👏 Grym poäng!','cheer'); else if(score>=30) pushToast('💪 Snyggt!','cheer'); };

  // Leader / tension toast on turn change
  // CHANGE (2026-08-15): Effekten kan köras flera gånger för samma tur (React
  // StrictMode dubbelkör i dev, och players-referensen byts när totalerna
  // räknas om) vilket gav två identiska notiser. En signatur per
  // (spelare, antal fyllda kategorier) gör den idempotent.
  const lastTurnToastRef = useRef<string>('');
  useEffect(() => {
    const players = gameState.players; if(!players.length) return;
    const filledCount = players.reduce((n, p) => n + Object.values(p.scores).filter(s => s !== null).length, 0);
    const signature = `${gameState.currentPlayerIndex}:${filledCount}`;
    if (lastTurnToastRef.current === signature) return;
    lastTurnToastRef.current = signature;
    const sorted=[...players].sort((a,b)=> b.totalScore - a.totalScore);
    const leader=sorted[0]; const second=sorted[1]; const me=players[gameState.currentPlayerIndex]; if(!me||!leader) return;
    // CHANGE (2026-08-15): "X, kör igång!" borttagen – rubriken och
    // spelarbadgen säger redan vems tur det är. Kvar: notiser som tillför
    // information (ledning/ikapp).
    let msg=''; let variant:'leader'|'cheer'|'turn'='leader';
    if(leader.totalScore===0){ return; }
    else if(leader.id===me.id && second){ const diff=leader.totalScore-second.totalScore; msg=`${me.name} leder med ${diff} poäng`; }
    else if(leader.id!==me.id){ const diff=leader.totalScore-me.totalScore; if(diff<=25){ msg=`${me.name}, chans att gå ikapp! (${diff} bakom)`; variant='cheer'; } else { msg=`${leader.name} leder med ${diff} poäng`; } }
    if(msg) pushToast(msg,variant);
  }, [gameState.currentPlayerIndex, gameState.players, pushToast]);

  // Recompute totals & detect finish (guarded to avoid infinite loops)
  useEffect(() => {
    setGameState(prev => {
      let changed = false;
      const newPlayers = prev.players.map(p => {
        if (isMaxi) {
          const { total, upper, bonus } = calculateMaxiTotals(p.scores as MaxiYatzyScores);
          if (p.totalScore !== total || p.upperSectionScore !== upper || p.upperSectionBonus !== bonus) {
            changed = true;
            return { ...p, totalScore: total, upperSectionScore: upper, upperSectionBonus: bonus };
          }
          return p;
        } else {
          // p.scores är YatzyScores i classic-läge (maxi grenas ovan)
          const { total, upperSection, bonus } = calculateTotalScore(p.scores as YatzyScores);
            if (p.totalScore !== total || p.upperSectionScore !== upperSection || p.upperSectionBonus !== bonus) {
              changed = true;
              return { ...p, totalScore: total, upperSectionScore: upperSection, upperSectionBonus: bonus };
            }
            return p;
        }
      });
      if (!changed) return prev;
      return { ...prev, players: newPlayers };
    });
    const playersSnapshot = gameState.players; // current reference
    const isComplete = playersSnapshot.every(p => Object.values(p.scores).every(s => s !== null));
    if (isComplete && gameState.gamePhase === 'playing') {
      const winner = playersSnapshot.reduce((a,b)=> a.totalScore>b.totalScore?a:b);
      setGameState(prev=> ({ ...prev, gamePhase: 'finished', winner }));
      setShowGameOver(true);
      try {
        // AI-spelare ska inte förorena topplistorna.
        playersSnapshot.filter(p => !p.isAI).forEach(p => {
          const modeId = isMaxi ? 'maxi' : 'classic';
          if (isHighScore(p.totalScore, modeId, 'local')) {
            addHighScore(p.name, p.totalScore, modeId, 'local');
          }
          if (isHighScore(p.totalScore, modeId, 'global')) {
            void submitGlobalHighScore(p.name, p.totalScore, modeId);
          }
        });
      } catch {}
    }
  }, [gameState.players, gameState.gamePhase, isMaxi]);

  const totalCategories = Object.keys(currentPlayer.scores).length;
  const currentRound = Math.max(...gameState.players.map(p => Object.values(p.scores).filter(s => s!==null).length)) + 1;
  const narrowDieVariant: 'mini' | 'compact' | 'normal' = isMaxi ? 'mini' : (isUltraNarrow ? 'mini' : 'normal');
  const desktopDieVariant: 'compact' | 'normal' = 'normal';

  // CHANGE (2026-08-15): Ersatt slumpplacering + "packa i en rad"-fallback med
  // ett rutnät (utils/dicePositions) som garanterat inte lägger tärningar
  // ovanpå varandra – prickarna gick tidigare inte att läsa i lokal multiplayer.
  const generatePositions = React.useCallback((count:number) => {
    if (typeof window !== 'undefined' && window.innerWidth < 520) return [];
    const rectW = diceAreaRef.current?.clientWidth || 560;
    const rectH = diceAreaRef.current?.clientHeight || (isMaxi ? 460 : 300);
    let dieSize = 104;
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 2560) dieSize = 148;
      else if (window.innerWidth >= 1920) dieSize = 132;
      else if (window.innerWidth >= 1400) dieSize = 112;
    }
    return computeDicePositions({ count, width: rectW, height: rectH, dieSize });
  }, [isMaxi]);

  useEffect(() => {
    // Debounce resize handler för bättre prestanda
    let resizeTimer: number | null = null;
    const handleResize = () => {
      if (resizeTimer) {
        window.clearTimeout(resizeTimer);
      }
      resizeTimer = window.setTimeout(() => {
        const w = window.innerWidth;
        const narrow = w < 520;
        const ultra = w < 400;
        setIsNarrow(narrow);
        setIsUltraNarrow(ultra);
        if(!narrow) setDicePositions(generatePositions(dice.length));
      }, 150); // 150ms debounce
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer) {
        window.clearTimeout(resizeTimer);
      }
    };
  }, [generatePositions, dice.length]);

  // Speed dial functions
  const dialActions: {[key: string]: {label: string, tip: string, icon: string}} = {
    ach: { label: 'Prestationer', tip: 'Visa prestationer', icon: '🏆' },
    hs: { label: 'Rekord', tip: 'Visa rekordlista', icon: '📊' },
    stats: { label: 'Statistik', tip: 'Visa spelstatistik', icon: '📈' },
    facts: { label: 'Fakta', tip: 'Visa roliga fakta', icon: '🎯' },
    chlog: { label: 'Ändringar', tip: 'Visa ändringslogg', icon: '📝' },
    ai: { label: 'AI-Assistent', tip: aiAssistantEnabled ? 'Stäng av AI-hjälp' : 'Slå på AI-hjälp', icon: aiAssistantEnabled ? '🤖✨' : '🤖' }
  };
  
  const handleDialAction = (key: string) => {
    switch (key) {
      case 'ach': setShowAchievements(true); break;
      case 'hs': setShowHighScores(true); break;
      case 'stats': setShowStatistics(true); break;
      case 'facts': setShowFunFacts(true); break;
      case 'chlog': setChangelogOpen(true); break;
      case 'ai': 
        const newState = !aiAssistantEnabled;
        setAiAssistantEnabled(newState);
        try {
          localStorage.setItem('yatzy-ai-assistant-enabled', newState.toString());
        } catch {}
        break;
    }
    setDialOpen(false);
  };

  const rollTimerRef = useRef<NodeJS.Timeout|null>(null);
  useEffect(() => {
    return () => {
      if (rollTimerRef.current) {
        clearTimeout(rollTimerRef.current);
        rollTimerRef.current = null;
      }
    };
  }, []);

  // CHANGE (2026-08-15): Uppdelad i performRoll (används även av AI:n) och
  // handleRollDice (mänsklig knapp, spärrad under AI:ns tur).
  const handleRollDice = () => {
    if (currentPlayer?.isAI) return;
    performRoll();
  };
  const performRoll = () => {
    if(rollsLeft>0){
      try{markPlayedToday();}catch{}
      // Räkna ut utfall och nya positioner direkt, innan snurr-animationen startar,
      // så att tärningen förflyttar sig till sin slutposition SAMTIDIGT som den snurrar
      // - annars snurrar den kvar på gamla platsen och "hoppar" till en ny plats
      // först när kastet är klart.
      const newDice = rollDice(dice);
      setIsRolling(true);
      setDice(newDice);
      setRollsLeft(r=> r-1);
      if(!isNarrow) setDicePositions(generatePositions(newDice.length));

      const delay = testOverrides?.rollDelayMs ?? 500;
      if (delay <= 0) {
        // Direkt uppdatering (testläge) för deterministiskt beteende
        setIsRolling(false);
        return;
      }
      rollTimerRef.current = setTimeout(() => {
        setIsRolling(false);
        rollTimerRef.current = null;
      }, Math.max(0, delay));
    }
  };
  const handleDieClick = (id:number) => { if(currentPlayer?.isAI) return; if(rollsLeft<maxRolls){ setDice(toggleDieLock(dice,id)); } };
  const handleSetLockedIds = (lockedIds: number[]) => {
    if (currentPlayer?.isAI) return;
    if (rollsLeft < maxRolls) {
      const targetSet = new Set(lockedIds);
      setDice(prev => prev.map(d => ({ ...d, isLocked: targetSet.has(d.id) })));
    }
  };
  const handleScoreSelect = (category: keyof YatzyScores | keyof MaxiYatzyScores) => {
    if(currentPlayer?.isAI) return; // AI:ns tur styrs av AI-drivern
    if(rollsLeft===maxRolls) return; // must roll at least once
    // Forced-order enforcement: only allow next empty category in defined order
    if (isForced) {
      const order: string[] = (isMaxi ? maxiScoreCategories.map(c=>c.key) : scoreCategories.map(c=>c.key)) as string[];
  const playerScores = currentPlayer.scores as unknown as Record<string, number|null>;
      const nextKey = order.find((k: string) => playerScores[k] === null);
      if (nextKey && nextKey !== category) {
        testOverrides?.onSelectCategory?.(category as string, false, rollsLeft);
        soundManager.playError?.();
        return; // reject selection
      }
    }
    // Confirm strike if score would be zero
    const zero = isMaxi ? calculateMaxiScore(dice, category as keyof MaxiYatzyScores) === 0 : calculatePossibleScore(dice, category as keyof YatzyScores) === 0;
    if (zero) {
      const name = category.toString();
      showConfirmationToast(`Stryka ${name}? Du får 0 poäng.`, {
        onConfirm: () => {
          testOverrides?.onSelectCategory?.(category as string, true, rollsLeft);
          handleScoreSelection(category);
        },
        confirmText: 'Ja, stryka',
         cancelText: 'Nej',
         replaceKey: 'strike-confirmation'
      });
      return;
    }
    testOverrides?.onSelectCategory?.(category as string, true, rollsLeft);
    handleScoreSelection(category);
  };

  const handleScoreSelection = (category: string | keyof YatzyScores | keyof MaxiYatzyScores) => {
    // Scroll till toppen innan spelarbyte för smidigare transition
    try {
      if (window.innerWidth <= 768) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    } catch {}

    setGameState(prev=>{
      const players=[...prev.players];
      const idx=prev.currentPlayerIndex;
      const player={...players[idx]};
      const score = isMaxi ? calculateMaxiScore(dice, category as keyof MaxiYatzyScores) : calculatePossibleScore(dice, category as keyof YatzyScores);
      (player.scores as any)[category]=score;
      players[idx]=player;
      cheerForScore(score);
      // Special sound for (super) yatzy
      if(category==='yatzy'){
        const counts = new Array(7).fill(0); dice.forEach(d=> counts[d.value]++);
        const six = counts.some(c=> c>=6);
        // I Maxi (6 dice) endast 6 lika ska trigga full Yatzy-firande
        if(isMaxi){
          if(six){ soundManager.playSuperYatzy?.(); }
        } else {
          if(score>=50){ soundManager.playYatzy(); }
        }
      }
      return { ...prev, players, currentPlayerIndex:(idx+1)%players.length };
    });
    setJustScoredCategory(category as string);
    setTimeout(() => setJustScoredCategory(null), 1700);
    setDice(resetDice(diceCount as any));
    setRollsLeft(maxRolls);
    if(!isNarrow) setDicePositions(generatePositions(diceCount));
  };
  // ── AI-driver ──────────────────────────────────────────────────────────
  // CHANGE (2026-08-15): När aktiv spelare är AI spelas turen automatiskt:
  // kasta → låsa tärningar → kasta → ... → välja kategori. Effekten körs om
  // vid varje state-förändring; beslutet cachas per (spelare, rollsLeft) så
  // att svårighetsgradens slump inte kan orsaka lås-loopar.
  const aiDecisionCacheRef = useRef<Map<string, ReturnType<typeof decideTurnAction>>>(new Map());
  useEffect(() => {
    const player = gameState.players[gameState.currentPlayerIndex];
    if (gameState.gamePhase !== 'playing' || !player?.isAI || isRolling || showGameOver) return;
    const level: AILevel = player.aiLevel || 'medium';
    const playerScores = player.scores as unknown as Record<string, number | null>;
    const forcedNext = isForced
      ? ((isMaxi ? maxiScoreCategories.map(c => c.key) : scoreCategories.map(c => c.key)) as string[])
          .find(k => playerScores[k] === null) || null
      : null;

    const timer = window.setTimeout(() => {
      // Första kastet i turen
      if (rollsLeft === maxRolls) { performRoll(); return; }

      // Kasten slut → välj kategori
      if (rollsLeft === 0) {
        const category = decideFinalCategory(dice, playerScores, player.upperSectionScore, isMaxi, level, forcedNext);
        aiDecisionCacheRef.current.clear();
        handleScoreSelection(category);
        return;
      }

      // Mitt i turen: säkra toppkombination eller lås + kasta igen.
      const filledCount = Object.values(playerScores).filter(v => v !== null).length;
      const cacheKey = `${gameState.currentPlayerIndex}:${filledCount}:${rollsLeft}`;
      let decision = aiDecisionCacheRef.current.get(cacheKey);
      if (!decision) {
        decision = decideTurnAction(dice, playerScores, rollsLeft, player.upperSectionScore, isMaxi, level, forcedNext);
        aiDecisionCacheRef.current.set(cacheKey, decision);
      }
      if (decision.action === 'score') {
        aiDecisionCacheRef.current.clear();
        handleScoreSelection(decision.category);
        return;
      }
      const keepSet = new Set(decision.keepIndices);
      const locksMatch = dice.every((d, i) => d.isLocked === keepSet.has(i));
      if (locksMatch) {
        performRoll();
      } else {
        setDice(prev => prev.map((d, i) => ({ ...d, isLocked: keepSet.has(i) })));
      }
    }, testOverrides?.rollDelayMs === 0 ? 0 : 750);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.currentPlayerIndex, gameState.gamePhase, gameState.players, rollsLeft, isRolling, dice, showGameOver]);

  // Test hooks: adjust rolls then attempt selection in next tick
  React.useEffect(() => {
    if (testOverrides) {
      if (typeof testOverrides.initialRollsLeft === 'number') {
        setRollsLeft(testOverrides.initialRollsLeft);
      }
      if (testOverrides.autoSelectCategory) {
        setTimeout(() => handleScoreSelect(testOverrides.autoSelectCategory as any), 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const doResetScores = () => {
    const blankClassic: YatzyScores = { ones:null, twos:null, threes:null, fours:null, fives:null, sixes:null, onePair:null, twoPairs:null, threeOfAKind:null, fourOfAKind:null, smallStraight:null, largeStraight:null, fullHouse:null, chance:null, yatzy:null };
  const blankMaxi: MaxiYatzyScores = { ones:null, twos:null, threes:null, fours:null, fives:null, sixes:null, onePair:null, twoPairs:null, threeOfAKind:null, fourOfAKind:null, fiveOfAKind:null, smallStraight:null, largeStraight:null, fullStraight:null, hus:null, torn:null, chance:null, yatzy:null };
    setGameState(prev=>({
      ...prev,
      players: prev.players.map(p=>({...p, scores: (isMaxi? { ...blankMaxi }: { ...blankClassic }), totalScore:0, upperSectionScore:0, upperSectionBonus:0 })),
      currentPlayerIndex:0, gamePhase:'playing', winner:undefined
    }));
    setDice(resetDice(diceCount as any));
    setRollsLeft(maxRolls);
    setShowGameOver(false);
    setTurnPopupVisible(true);
  };
  const handleNewGameConfirm = () => { try{markPlayedToday();}catch{} doResetScores(); setConfirmNewGame(false); };
  const handleNewGameRequest = () => setConfirmNewGame(true);

  return <GameContainer>
    {!hideMenu && (
      <FabContainer>
        {/* Hem-knapp: uppe vänster */}
        <Fab $pos="tl" aria-label="Hem" data-tip="Tillbaka till huvudmenyn" onClick={() => { 
          showConfirmationToast('Avsluta detta multiplayer-spel och återgå till startmenyn?', {
            onConfirm: () => {
              window.location.href = window.location.origin + window.location.pathname;
            },
            confirmText: 'Ja, avsluta',
            cancelText: 'Nej'
          });
        }}>🏠</Fab>
        
        {/* Hamburger: uppe höger */}
        <Fab $pos="tr" aria-label="Meny" data-tip="Meny" data-tip-pos="left" onClick={() => setMenuOpen(true)}>☰</Fab>
        
        {/* ?-knapp: nere vänster */}
        <Fab $pos="bl" aria-label="Hjälp" data-tip="Hjälp" onClick={() => setShowHelp(v => !v)}>?</Fab>
        
        {/* +-knapp: nere höger */}
        <SpeedDialWrapper data-speeddial>
          <DialActions $open={dialOpen} aria-hidden={!dialOpen} role="menu">
            {['ach', 'hs', 'stats', 'facts', 'chlog'].map(k => {
              const a = dialActions[k]; 
              if (!a) return null;
              return (
                <DialBtn 
                  key={k} 
                  aria-label={a.label} 
                  data-tip={a.tip} 
                  role="menuitem" 
                  onClick={()=>handleDialAction(k)}
                >
                  {a.icon}
                </DialBtn>
              );
            })}
          </DialActions>
          <MainDial aria-label="Snabbmeny" aria-haspopup="true" aria-expanded={dialOpen} onClick={()=>setDialOpen(o=>!o)}>{dialOpen? '×':'+'}</MainDial>
        </SpeedDialWrapper>
      </FabContainer>
    )}
    
    {!hideMenu && <MobileMenu
      isOpen={menuOpen}
      onClose={()=>setMenuOpen(false)}
      activeGame={gameState.players.some((p:Player)=> Object.values(p.scores as any).some((v:any)=> v!==null))}
      onHome={()=> {
        showConfirmationToast('Gå till startsidan? Pågående multiplayer-parti avslutas.', {
          onConfirm: () => {
            window.location.href = window.location.origin + window.location.pathname;
          },
          confirmText: 'Ja, avsluta',
          cancelText: 'Nej'
        });
      }}
    />}
    
    <Header>
      <Title>🎲 {isMaxi ? 'Maxi Yatzy' : 'Yatzy'} - Multiplayer</Title>
      <Subtitle>Tävla mot dina vänner!</Subtitle>
    </Header>
    <PlayerStatusDisplay players={gameState.players} currentPlayerIndex={gameState.currentPlayerIndex} />
    <GameBoard>
      <DiceSection>
        <GameInfo>
          <GlowEffects active={rollsLeft > 0} type="text">
            <RollInfo>{currentPlayer.name}s tur - Kast {Math.min(maxRolls - rollsLeft + 1, maxRolls)} av {maxRolls}</RollInfo>
            <RoundInfo>Omgång {currentRound} av {totalCategories}</RoundInfo>
          </GlowEffects>
        </GameInfo>
        {aiAssistantEnabled && !isMaxi && <StrategyHelper 
          dice={dice} 
          scores={currentPlayer.scores as YatzyScores} 
          rollsLeft={rollsLeft}
          upperSectionScore={currentPlayer.upperSectionScore}
        />}
      <DiceBoard3D
        dice={dice}
        isRolling={isRolling}
        canInteract={!currentPlayer?.isAI && rollsLeft < maxRolls && !isRolling}
        onToggleDie={handleDieClick}
        onSetLockedIds={handleSetLockedIds}
      />
      {false && (isNarrow ? (
        <SmallDiceRow>
          {dice.map((d, i) => (
            <div key={d.id}>
              <Die2D value={d.value} isLocked={d.isLocked} isRolling={isRolling && !d.isLocked} onClick={()=>handleDieClick(d.id)} variant={narrowDieVariant} index={i} />
            </div>
          ))}
        </SmallDiceRow>
      ) : (
        <DiceContainer ref={diceAreaRef} $tall={isMaxi}>
          {dice.map((d, idx) => {
            const pos = dicePositions[idx];
            return (
              <PositionedDie key={d.id} $x={pos?.x || 40 + idx*90} $y={pos?.y || 120} $rolling={isRolling && !d.isLocked}>
                <GlowEffects active={!d.isLocked && rollsLeft < maxRolls} type="dice">
                  <Die2D value={d.value} isLocked={d.isLocked} isRolling={isRolling && !d.isLocked} onClick={()=>handleDieClick(d.id)} variant={desktopDieVariant} index={idx} />
                </GlowEffects>
              </PositionedDie>
            );
          })}
        </DiceContainer>
      ))}
        <ButtonContainer>
          <GlowEffects active={rollsLeft > 0} type="button">
            <Button onClick={handleRollDice} disabled={rollsLeft===0||isRolling||!!currentPlayer?.isAI}>
              {currentPlayer?.isAI ? '🤖 AI spelar...' : isRolling ? 'Kastar...' : `Kasta (${rollsLeft} kvar)`}
            </Button>
          </GlowEffects>
          <Button $variant="secondary" onClick={()=>{soundManager.playButtonClick();setShowHighScores(true);}}>Highscore</Button>
          <Button $variant="secondary" onClick={()=>{soundManager.playButtonClick();setShowStatistics(true);}}>📊 Statistik</Button>
          <Button $variant="danger" onClick={onGameEnd}>Avsluta spel</Button>
        </ButtonContainer>
      </DiceSection>
      <SidebarSection>
  {isMaxi ? <MaxiScoreCard ref={scoreCardRef} scores={currentPlayer.scores as MaxiYatzyScores} dice={dice} onScoreSelect={handleScoreSelect} rollsLeft={rollsLeft} upperSectionScore={currentPlayer.upperSectionScore} upperSectionBonus={currentPlayer.upperSectionBonus} totalScore={currentPlayer.totalScore} highlightCategory={justScoredCategory} forcedOrder={isForced ? (maxiScoreCategories.map(c=>c.key) as any) : undefined} maxRolls={maxRolls} /> :
  <ScoreCard ref={scoreCardRef} scores={currentPlayer.scores as YatzyScores} dice={dice} onScoreSelect={handleScoreSelect} rollsLeft={rollsLeft} upperSectionScore={currentPlayer.upperSectionScore} upperSectionBonus={currentPlayer.upperSectionBonus} totalScore={currentPlayer.totalScore} highlightCategory={justScoredCategory} forcedOrder={isForced ? (scoreCategories.map(c=>c.key) as any): undefined} maxRolls={maxRolls} />}
      </SidebarSection>
    </GameBoard>
  <HighScoreList isVisible={showHighScores} onClose={()=>setShowHighScores(false)} modeId={isMaxi?'maxi':'classic'} />
    <StatisticsModal isVisible={showStatistics} onClose={()=>setShowStatistics(false)} />
    <HelpRulesModal open={showHelp} onClose={()=>setShowHelp(false)} />
  <GameOverModal isVisible={showGameOver} totalScore={gameState.winner?.totalScore||0} winnerName={gameState.winner?.name} onNewGame={handleNewGameRequest} onViewHighScores={()=>{setShowGameOver(false);setShowHighScores(true);}} modeId={isMaxi?'maxi':'classic'} />
    <ToastStack>{toasts.map(t=> <ToastMsg key={t.id} $variant={t.variant} $leaving={t.leaving}>{t.msg}</ToastMsg>)}</ToastStack>
    <Overlay $show={turnPopupVisible && gameState.gamePhase==='playing'}>
      <PopupCard role="dialog" aria-modal="true">
        <PopupTitle>🎲 Dags för {currentPlayer.name}</PopupTitle>
        <PopupText>Nästa kast kan förändra allt – lycka till!</PopupText>
        <PopupButton onClick={()=>{setTurnPopupVisible(false);soundManager.playButtonClick();}}>Kör!</PopupButton>
      </PopupCard>
    </Overlay>
    <Overlay $show={confirmNewGame}>
      <PopupCard role="dialog" aria-modal="true">
        <PopupTitle>Starta nytt spel?</PopupTitle>
        <PopupText>Vill du verkligen starta en ny omgång?</PopupText>
        <PopupActions>
          <PopupButton onClick={handleNewGameConfirm}>Ja, nytt spel</PopupButton>
          <PopupButton style={{background:'linear-gradient(135deg,#9e9e9e,#bdbdbd)'}} onClick={()=> setConfirmNewGame(false)}>Nej, behåll</PopupButton>
        </PopupActions>
      </PopupCard>
    </Overlay>
    
    {/* Speed Dial Modals */}
    <AchievementsModal isVisible={showAchievements} onClose={() => setShowAchievements(false)} modeId={isMaxi ? 'maxi' : 'classic'} />
    <FunFactsModal open={showFunFacts} onClose={() => setShowFunFacts(false)} />
    <ChangelogModal open={changelogOpen} onClose={() => setChangelogOpen(false)} />
    
  </GameContainer>;
};

export default MultiplayerGame;
